import { ChevronDownIcon } from "@chakra-ui/icons";
import { Button, Flex, FormControl, FormLabel, Input, Select } from "@chakra-ui/react";
import { RpcError, StatusCode } from "grpc-web";
import { CSSProperties, KeyboardEvent, useState } from "react";
import { FaSearch } from "react-icons/fa";

// Components
import CustomModal from "../../shared/Modals/CustomModal";

// Hooks
import useClaimsToast from "../../hooks/useToast";

// Protos
import { Provider, ProvidersGetResponse } from "../../utils/api/protos/messages/providers.message_pb";

// Utils
import { getProvider } from "../../utils/api/providers";
import { UserRole } from "../../utils/consts";
import { createUser } from "../../utils/api/auth";

interface AddUserModalProps {
	isOpen: boolean;
	onClose(): void;
	onContinue(): void;
}

export default function AddUserModal({ isOpen, onClose, onContinue }: AddUserModalProps) {
	const toast = useClaimsToast();

	const [isProviderSearchLoading, setIsProviderSearchLoading] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [userRole, setUserRole] = useState<UserRole | undefined>(undefined);
	const [attorneyIdSearch, setAttorneyIdSearch] = useState<string>("");
	const [id, setId] = useState<string>("");
	const [name, setName] = useState<string>("");
	const [email, setEmail] = useState<string>("");

	const handleSearchProvider = async (providerId: string): Promise<Provider | undefined> => {
		setIsProviderSearchLoading(true);
		setName("");
		setEmail("");
		return getProvider(+providerId)
			.then((response: ProvidersGetResponse) => {
				const foundProvider: Provider | undefined = response.getProvider();
				if (foundProvider) {
					setName(foundProvider.getNamefirst() + " " + foundProvider.getNamelast());
					setEmail(foundProvider.getEmail());
					setId(foundProvider.getAttyid().toString());
				}
				return foundProvider;
			})
			.catch((err: RpcError) => {
				console.error(err);
				if (err.code === StatusCode.NOT_FOUND) {
					toast(
						undefined,
						"An Attorney was not found. Please have Network Management add the Attorney to LAMP™",
						"warning"
					);
				} else toast("Error", (err as RpcError).message, "error");

				return undefined;
			})
			.finally(() => {
				setIsProviderSearchLoading(false);
			});
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);
		return createUser(email, name, email, userRole?.toString() ?? "", id)
			.then(() => {
				toast("Success", "User successfully added", "success");
				onContinue();
			})
			.catch((err) => {
				console.error(err);
				// Try to extract the actual error message
				// Pattern: "message: \"actual error message\""
				let wordRegex: RegExp = /message: "([^"]+)"/g; // Matches whole words globally
				let match = wordRegex.exec(err.message);
				if (match && match[1]) {
					toast("Error", match[1], "error");
				} else {
					toast("Error", (err as RpcError).message, "error");
				}

				return undefined;
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			handleSearchProvider(attorneyIdSearch);
		}
	};

	return isOpen ? (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={onClose}
			onContinue={handleSubmit}
			size="3xl"
			title="Add User"
			continueText="Add"
			continueDisabled={!userRole || !name || !email || (userRole === UserRole.Provider && !id)}
			continueLoading={isSubmitting}
			content={
				<>
					<Flex flexWrap="wrap" alignItems="flex-end">
						<FormControl style={styles.field}>
							<FormLabel style={styles.label}>Role</FormLabel>
							<Select
								style={styles.input}
								value={userRole}
								onChange={(event) => {
									setUserRole(event.target.value as UserRole);
									// Also clear name and email for providers
									if (event.target.value === UserRole.Provider) {
										setName("");
										setEmail("");
									}
								}}
								w="216px"
								h="32px"
								size="sm"
								icon={<ChevronDownIcon />}>
								<option value="">None</option>
								{[UserRole.Provider].map((role, index) => (
									<option key={index} value={role.toString()}>
										{role.toString()}
									</option>
								))}
							</Select>
						</FormControl>
						{userRole === UserRole.Provider && (
							<>
								<FormControl style={styles.field} maxWidth="150px">
									<FormLabel style={styles.label}>Attorney ID</FormLabel>
									<Input
										size="sm"
										type="number"
										style={styles.input}
										value={attorneyIdSearch}
										onChange={(event) => setAttorneyIdSearch(event.target.value)}
										onKeyDown={(event) => handleSearchKeyDown(event)}
									/>
								</FormControl>
								<Button
									style={styles.field}
									colorScheme="blackAlpha"
									maxWidth="150px"
									height="32px"
									onClick={() => handleSearchProvider(attorneyIdSearch)}
									isLoading={isProviderSearchLoading}
									isDisabled={!attorneyIdSearch}>
									<FaSearch color="white" />
									&nbsp;Search
								</Button>
							</>
						)}
					</Flex>
					<Flex flexWrap="wrap" alignItems="flex-end" mb={5}>
						<FormControl style={styles.field}>
							<FormLabel style={styles.label}>Name</FormLabel>
							<Input
								size="sm"
								style={styles.input}
								value={name}
								_disabled={styles.disabled}
								onChange={(event) => setName(event.target.value)}
								isDisabled={userRole === UserRole.Provider}
							/>
						</FormControl>
						<FormControl style={styles.field} width="50% !important">
							<FormLabel style={styles.label}>Email</FormLabel>
							<Input
								size="sm"
								style={styles.input}
								value={email}
								_disabled={styles.disabled}
								onChange={(event) => setEmail(event.target.value)}
								isDisabled={userRole === UserRole.Provider}
							/>
						</FormControl>
					</Flex>
				</>
			}
		/>
	) : (
		<></>
	);
}

const styles: { [id: string]: CSSProperties } = {
	field: {
		width: "30%",
		marginLeft: "10px",
		marginRight: "10px",
		marginTop: "10px"
	},
	label: {
		marginTop: "4px",
		marginBottom: "3px"
	},
	input: {
		borderColor: "#bbbbbb",
		borderRadius: "3px"
	},
	disabled: {
		opacity: "1",
		backgroundColor: "#ededed",
		cursor: "not-allowed"
	}
};
